import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { agentDisplayName } from '@/config/env'
import { BILLING_ROW_LABELS } from '../types'

// Explains what each billed line covers. Keyed by the line's on-screen
// description via the shared BILLING_ROW_LABELS constants (also used by
// useBillingPageData for the `description` values), so it only lights up for
// rows we have copy for (the Calls parent and Compensation credits stay plain text) and a
// label rename in one place can't silently desync from the other.
// An add-on is priced as a percentage of the bill but paid for in minutes,
// which its row shows, so this table's Total minutes matches the Minutes
// Ledger's Used.
const ADDON_TOOLTIP_NOTE =
  ' Charged as a percentage of the month’s usage, and taken from your minute ' +
  'balance at your per-minute rate.'

const ROW_TOOLTIPS: Record<string, string> = {
  [BILLING_ROW_LABELS.conversation]:
    'Minutes Angelica is actually speaking on the phone.',
  [BILLING_ROW_LABELS.analysis]:
    'Right after the call — transcription, labeling, and any other review or analysis. ' +
    'Rubric grading is billed separately.',
  [BILLING_ROW_LABELS.connection]:
    'Phone-line infrastructure costs. Reported for the whole organization, ' +
    'so the split by duty is an estimate based on each duty’s share of ' +
    'talk time — not a per-duty measurement like the rows above.',
  [BILLING_ROW_LABELS.projectedConnection]:
    'Phone-line infrastructure costs, estimated from this month’s phone ' +
    'minutes so far (updated nightly) at today’s rates. The final charge is ' +
    'billed when the month closes.',
  [BILLING_ROW_LABELS.computerSessions]:
    'Minutes spent working inside a computer system to carry out whatever the call decided.',
  [BILLING_ROW_LABELS.evaluationRubrics]:
    'Each call graded against your configured rubrics after it ended.',
  [BILLING_ROW_LABELS.davidSessions]: `Each request turn someone made of ${agentDisplayName('david')} in the app.`,
  [BILLING_ROW_LABELS.advancedSupport]:
    'Escalated technical assistance beyond standard ticketing.' +
    ADDON_TOOLTIP_NOTE,
  [BILLING_ROW_LABELS.projectRollout]:
    'Dedicated planning and timeline development for complex or large-scale ' +
    'expansions beyond two (2) use cases concurrently.' +
    ADDON_TOOLTIP_NOTE,
}

export function RowLabel({ description }: { description: string }) {
  const copy = ROW_TOOLTIPS[description]
  if (!copy) return <>{description}</>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="cursor-help underline decoration-dotted underline-offset-4">
          {description}
        </span>
      </TooltipTrigger>
      {/* `text-wrap`, not the primitive's `text-balance`: the box is `w-fit`,
          so it takes the cap, and balancing then splits the copy into short
          lines that leave a wide gap on the right. Filling the first line
          keeps the box on the text. */}
      <TooltipContent side="right" className="max-w-xs text-wrap">
        {copy}
      </TooltipContent>
    </Tooltip>
  )
}
